import React from "react"
import { useTranslation } from "react-i18next"
import {
  MdPeople,
  MdHome,
  MdAssignment,
  MdFolder,
  MdCalculate,
  MdReceipt,
  MdEvent,
  MdSecurity,
  MdNotes,
  MdPayment,
  MdSchedule,
  MdInventory,
  MdBuild,
  MdShoppingCart,
  MdBarChart,
  MdDirectionsCar,
  MdDescription,
  MdLocalOffer,
  MdAttachMoney,
  MdTune
} from "react-icons/md"

// Components that have unique functionality
import FilesExport from "./components/Files"
import ProductFilesExport from "./components/ProductFiles"
import ExportAll from "./components/ExportAll"
import RecentExports from "./components/RecentExports"
import ExportCard from "./components/ExportCard"

// Export types that are ready for use (shown in UI and included in "Export All")
const EXPORT_TYPES_ENABLED = [
  "inventory",
  "services",
  "groups",
  "customers",
  "addresses",
  "requests",
  "projects",
  "equipment",
  "service_plans"
] as const

// Configuration for all standard exports (disabled ones are hidden but backend still supports them)
const EXPORT_TYPES = [
  { type: "customers", icon: MdPeople, titleKey: "Customers", descKey: "Export all customer records with contact info and tags" },
  { type: "addresses", icon: MdHome, titleKey: "Properties", descKey: "Export all property addresses with customer info" },
  { type: "requests", icon: MdAssignment, titleKey: "Requests", descKey: "Export all service requests with status and details" },
  { type: "projects", icon: MdFolder, titleKey: "Projects", descKey: "Export all projects with status and totals" },
  { type: "estimates", icon: MdCalculate, titleKey: "Estimates", descKey: "Export all estimates with totals and status" },
  { type: "invoices", icon: MdReceipt, titleKey: "Invoices", descKey: "Export all invoices with payments and balances" },
  { type: "appointments", icon: MdEvent, titleKey: "Appointments", descKey: "Export all appointments with assignees and status" },
  { type: "warranties", icon: MdSecurity, titleKey: "Warranties", descKey: "Export all warranty items with expiration dates" },
  { type: "notes", icon: MdNotes, titleKey: "Notes", descKey: "Export all notes from requests, invoices, and more" },
  { type: "payments", icon: MdPayment, titleKey: "Payments", descKey: "Export all payment records with details" },
  { type: "service_plans", icon: MdSchedule, titleKey: "Service Plans", descKey: "Export all service plans and subscriptions" },
  { type: "inventory", icon: MdInventory, titleKey: "Inventory", descKey: "Export all inventory items with pricing" },
  { type: "services", icon: MdBuild, titleKey: "Services", descKey: "Export all services with pricing" },
  { type: "groups", icon: MdShoppingCart, titleKey: "Groups", descKey: "Export all item groups with children and pricing" },
  { type: "customer_summary", icon: MdBarChart, titleKey: "Customer Summary", descKey: "Export customer overview with aggregated stats" },
  { type: "equipment", icon: MdDirectionsCar, titleKey: "Equipment", descKey: "Export all equipment including vehicles, warehouses, inventory quantities, and transfers" },
  { type: "custom_docs", icon: MdDescription, titleKey: "Custom Docs", descKey: "Export all custom document templates with settings and options" },
  { type: "tags", icon: MdLocalOffer, titleKey: "Tags", descKey: "Export all tags with type and where they are used (customers, properties, parts)" },
  { type: "taxes", icon: MdAttachMoney, titleKey: "Taxes, Fees, & Discounts", descKey: "Export all taxes, fees, and discounts with rates and group membership" },
  { type: "custom_fields", icon: MdTune, titleKey: "Custom Fields", descKey: "Export all custom fields with key, value, type, and division visibility" },
] as const

const ExportIndex = () => {
  const { t } = useTranslation()
  const tBase = "views.admin.export.index"
  const tr = (key: string) => t(`${tBase}.${key}`)

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          {tr("Data Export Tool")}
        </h1>
        <p className="text-gray-600">
          {tr("Export your company data as Excel files. Each export will be processed in the background and you will receive an email when ready.")}
        </p>
      </div>

      {/* Export All Section */}
      <div className="mb-8">
        <ExportAll />
      </div>

      {/* Export Types Grid */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-700 mb-4">
          {tr("Individual Exports")}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {EXPORT_TYPES.filter((exportConfig) => (EXPORT_TYPES_ENABLED as readonly string[]).includes(exportConfig.type)).map((exportConfig) => {
            const Icon = exportConfig.icon
            return (
              <ExportCard
                key={exportConfig.type}
                exportType={exportConfig.type}
                title={tr(exportConfig.titleKey)}
                description={tr(exportConfig.descKey)}
                icon={<Icon />}
              />
            )
          })}
        </div>
      </div>

      {/* Files Export Section (Separate as it has date filters) */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-700 mb-4">
          {tr("File Export")}
        </h2>
        <FilesExport />
      </div>

      {/* Product Files Export Section */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-700 mb-4">
          {tr("Product Files Export")}
        </h2>
        <ProductFilesExport />
      </div>

      {/* Recent Exports Section */}
      <div>
        <RecentExports />
      </div>
    </div>
  )
}

export default ExportIndex
